import type { DamageType } from '../config/constants';

export interface EnemyDef {
  id: string;
  name: string;
  desc: string;
  hp: number;
  speed: number;
  armour: number;
  bounty: number;
  breachDamage: number;
  radius: number;
  colour: number;
  scale: number;
  resist: Partial<Record<DamageType, number>>;
  /** Visual/behavioural archetype. Defaults to the mesh-kin shapes. */
  family?: 'mesh' | 'insect' | 'robot' | 'human';
  elite?: boolean;
  boss?: boolean;
  regen?: number;
  shield?: number;
  shieldRegen?: number;
  spawnChild?: string;
  spawnInterval?: number;
  spawnMax?: number;
  stunTowers?: boolean;
  stunRadius?: number;
  ranged?: boolean;
  /** Range (px) and cadence (s) for ranged suppression fire. */
  range?: number;
  fireInterval?: number;
  /** This unit launches a visible counter-fire shot at a nearby tower. */
  returnFire?: boolean;
  phases?: {
    hpPct: number;
    speedMul?: number;
    armourMul?: number;
    summon?: string;
    summonCount?: number;
    message?: string;
  }[];
}

export const ENEMIES: Record<string, EnemyDef> = {
  scuttler: {
    id: 'scuttler',
    name: 'Scuttler',
    desc: 'Fast weak mesh-kin. Overwhelms by numbers.',
    hp: 28,
    speed: 95,
    armour: 0,
    bounty: 4,
    breachDamage: 1,
    radius: 9,
    colour: 0x8fd45a,
    scale: 1,
    resist: { THERMAL: 1.35, EXPLOSIVE: 1.15 },
  },
  husk: {
    id: 'husk',
    name: 'Husk',
    desc: 'Standard Undermesh combat form.',
    hp: 90,
    speed: 55,
    armour: 4,
    bounty: 8,
    breachDamage: 2,
    radius: 12,
    colour: 0x6ab84a,
    scale: 1.1,
    resist: {},
  },
  dashling: {
    id: 'dashling',
    name: 'Dashling',
    desc: 'Extremely fast runner. Breaks unprepared lines.',
    hp: 45,
    speed: 150,
    armour: 1,
    bounty: 7,
    breachDamage: 2,
    radius: 10,
    colour: 0xc8e85a,
    scale: 0.9,
    resist: { THERMAL: 1.2 },
  },
  plateback: {
    id: 'plateback',
    name: 'Plateback',
    desc: 'Heavy plated beast. Resists ballistic fire.',
    hp: 320,
    speed: 38,
    armour: 22,
    bounty: 18,
    breachDamage: 4,
    radius: 14,
    colour: 0x4a8a3a,
    scale: 1.5,
    resist: { BALLISTIC: 0.35, THERMAL: 0.7, ENERGY: 1.4 },
  },
  goliath: {
    id: 'goliath',
    name: 'Goliath',
    desc: 'Slow siege organism with extreme durability.',
    hp: 550,
    speed: 28,
    armour: 12,
    bounty: 24,
    breachDamage: 6,
    radius: 16,
    colour: 0x3a6a2e,
    scale: 1.7,
    resist: { EXPLOSIVE: 1.2, ENERGY: 1.15 },
  },
  mender: {
    id: 'mender',
    name: 'Mender',
    desc: 'Regenerates rapidly. Burn or burst it down.',
    hp: 180,
    speed: 48,
    armour: 6,
    bounty: 14,
    breachDamage: 3,
    radius: 11,
    colour: 0x5ad4a0,
    scale: 1.15,
    regen: 12,
    resist: { THERMAL: 0.6, BALLISTIC: 0.85 },
  },
  broodnode: {
    id: 'broodnode',
    name: 'Broodnode',
    desc: 'Vomits Scuttlers as it advances.',
    hp: 260,
    speed: 32,
    armour: 8,
    bounty: 20,
    breachDamage: 5,
    radius: 14,
    colour: 0xa0c84a,
    scale: 1.4,
    spawnChild: 'scuttler',
    spawnInterval: 2.8,
    spawnMax: 4,
    resist: { EXPLOSIVE: 0.75 },
  },
  veilguard: {
    id: 'veilguard',
    name: 'Veilguard',
    desc: 'Regenerating energy shields. Energy weapons excel.',
    hp: 200,
    speed: 50,
    armour: 10,
    bounty: 16,
    breachDamage: 3,
    radius: 12,
    colour: 0x4aa0c8,
    scale: 1.25,
    shield: 120,
    shieldRegen: 10,
    resist: { BALLISTIC: 0.55, THERMAL: 0.7, ENERGY: 1.5 },
  },
  sporethrower: {
    id: 'sporethrower',
    name: 'Sporethrower',
    desc: 'Ranged organism that harasses barriers and pads.',
    hp: 150,
    speed: 42,
    armour: 5,
    bounty: 12,
    breachDamage: 3,
    radius: 11,
    colour: 0xd4c84a,
    scale: 1.2,
    ranged: true,
    resist: { EXPLOSIVE: 1.15 },
  },
  nulltick: {
    id: 'nulltick',
    name: 'Nulltick',
    desc: 'Emits a jamming field that weakens nearby towers.',
    hp: 140,
    speed: 52,
    armour: 4,
    bounty: 15,
    breachDamage: 2,
    radius: 10,
    colour: 0xc85ac8,
    scale: 1.1,
    stunTowers: true,
    stunRadius: 70,
    resist: { ENERGY: 0.7 },
  },
  archon: {
    id: 'archon',
    name: 'Archon',
    desc: 'Elite mutant. Extreme armour and kinetic bursts.',
    hp: 900,
    speed: 44,
    armour: 28,
    bounty: 50,
    breachDamage: 10,
    radius: 16,
    colour: 0xff5a5a,
    scale: 1.6,
    elite: true,
    regen: 6,
    resist: { BALLISTIC: 0.4, THERMAL: 0.75, EXPLOSIVE: 0.85, ENERGY: 1.25 },
  },
  colossus: {
    id: 'colossus',
    name: 'Mesh Colossus',
    desc: 'Boss organism. Phases summon escorts and break fortifications.',
    hp: 4200,
    speed: 26,
    armour: 20,
    bounty: 200,
    breachDamage: 25,
    radius: 22,
    colour: 0xff3a3a,
    scale: 2.4,
    boss: true,
    elite: true,
    resist: { BALLISTIC: 0.45, THERMAL: 0.8, EXPLOSIVE: 0.9, ENERGY: 1.2 },
    phases: [
      {
        hpPct: 0.65,
        speedMul: 1.25,
        message: 'COLOSSUS SURGE — SPEED INCREASE',
      },
      {
        hpPct: 0.35,
        summon: 'dashling',
        summonCount: 4,
        speedMul: 1.4,
        message: 'COLOSSUS CALLS ESCORTS',
      },
    ],
  },

  // ── INSECT SWARM — fast, fragile, burn easily ─────────────
  locust: {
    id: 'locust',
    name: 'Locust',
    desc: 'Chitin swarm-runner. Barely armoured; burns in seconds.',
    hp: 22,
    speed: 190,
    armour: 0,
    bounty: 5,
    breachDamage: 1,
    radius: 7,
    colour: 0xd8e06a,
    scale: 0.8,
    family: 'insect',
    resist: { THERMAL: 1.6, BALLISTIC: 0.85 },
  },
  wasp: {
    id: 'wasp',
    name: 'Razor Wasp',
    desc: 'Winged skirmisher. Dives the lane faster than anything on legs.',
    hp: 36,
    speed: 165,
    armour: 0,
    bounty: 6,
    breachDamage: 1,
    radius: 8,
    colour: 0xffd24a,
    scale: 0.85,
    family: 'insect',
    resist: { THERMAL: 1.5, BALLISTIC: 0.8 },
  },
  venomspitter: {
    id: 'venomspitter',
    name: 'Venomspitter',
    desc: 'Spits corrosive venom that fouls your turrets from range.',
    hp: 130,
    speed: 46,
    armour: 4,
    bounty: 14,
    breachDamage: 3,
    radius: 11,
    colour: 0x9fd44a,
    scale: 1.15,
    family: 'insect',
    ranged: true,
    returnFire: true,
    range: 150,
    fireInterval: 2.4,
    resist: { THERMAL: 1.4, ENERGY: 0.85 },
  },
  hivecrawler: {
    id: 'hivecrawler',
    name: 'Hivecrawler',
    desc: 'Bloat-queen that keeps birthing wasps as it crawls.',
    hp: 240,
    speed: 40,
    armour: 7,
    bounty: 20,
    breachDamage: 5,
    radius: 13,
    colour: 0xb8a03a,
    scale: 1.35,
    family: 'insect',
    spawnChild: 'wasp',
    spawnInterval: 2.6,
    spawnMax: 4,
    resist: { THERMAL: 1.3, EXPLOSIVE: 0.8 },
  },

  // ── MACHINES — shielded, armoured, overload on energy ─────
  dronebot: {
    id: 'dronebot',
    name: 'Dronebot',
    desc: 'Cheap autonomous skirmisher with a light energy screen.',
    hp: 60,
    speed: 120,
    armour: 3,
    bounty: 8,
    breachDamage: 2,
    radius: 9,
    colour: 0x8ad0ff,
    scale: 0.95,
    family: 'robot',
    shield: 40,
    shieldRegen: 6,
    resist: { BALLISTIC: 0.5, ENERGY: 1.5, THERMAL: 0.8 },
  },
  sentrybot: {
    id: 'sentrybot',
    name: 'Sentrybot',
    desc: 'Tracked gun platform. Suppresses nearby turrets with plasma.',
    hp: 260,
    speed: 44,
    armour: 16,
    bounty: 20,
    breachDamage: 4,
    radius: 13,
    colour: 0x6fb8e8,
    scale: 1.3,
    family: 'robot',
    shield: 120,
    shieldRegen: 9,
    ranged: true,
    returnFire: true,
    range: 140,
    fireInterval: 2.6,
    resist: { BALLISTIC: 0.4, ENERGY: 1.6, EXPLOSIVE: 1.2 },
  },
  haulerbot: {
    id: 'haulerbot',
    name: 'Haulerbot',
    desc: 'Siege chassis. Absurd plating; bring energy or explosives.',
    hp: 620,
    speed: 30,
    armour: 30,
    bounty: 30,
    breachDamage: 7,
    radius: 17,
    colour: 0x7a94a8,
    scale: 1.7,
    family: 'robot',
    resist: { BALLISTIC: 0.3, THERMAL: 0.7, ENERGY: 1.4, EXPLOSIVE: 0.9 },
  },
  reaperbot: {
    id: 'reaperbot',
    name: 'Reaper Unit',
    desc: 'Elite kill-machine. Broad-spectrum jamming field.',
    hp: 1100,
    speed: 40,
    armour: 24,
    bounty: 60,
    breachDamage: 12,
    radius: 17,
    colour: 0xff6a4a,
    scale: 1.6,
    family: 'robot',
    elite: true,
    shield: 200,
    shieldRegen: 12,
    stunTowers: true,
    stunRadius: 80,
    resist: { BALLISTIC: 0.35, THERMAL: 0.7, EXPLOSIVE: 0.8, ENERGY: 1.5 },
  },

  // ── RAIDERS — ranged, agile, shattered by explosives ──────
  gunner: {
    id: 'gunner',
    name: 'Scrap Gunner',
    desc: 'Raider with a salvaged autocannon. Fast, sustained fire.',
    hp: 190,
    speed: 58,
    armour: 10,
    bounty: 14,
    breachDamage: 3,
    radius: 11,
    colour: 0xd4a06a,
    scale: 1.1,
    family: 'human',
    ranged: true,
    range: 130,
    fireInterval: 1.6,
    resist: { EXPLOSIVE: 1.45, ENERGY: 0.75 },
  },
  raider: {
    id: 'raider',
    name: 'Laser Raider',
    desc: 'Mercenary with a hot laser rifle. Picks off turrets.',
    hp: 150,
    speed: 70,
    armour: 6,
    bounty: 12,
    breachDamage: 3,
    radius: 10,
    colour: 0xe8c07a,
    scale: 1.05,
    family: 'human',
    ranged: true,
    range: 160,
    fireInterval: 2.2,
    resist: { EXPLOSIVE: 1.5, ENERGY: 0.7 },
  },
  sharpshooter: {
    id: 'sharpshooter',
    name: 'Sharpshooter',
    desc: 'Long-range marksman. Hits turrets from outside their reach.',
    hp: 120,
    speed: 52,
    armour: 4,
    bounty: 15,
    breachDamage: 3,
    radius: 10,
    colour: 0xf0d090,
    scale: 1.05,
    family: 'human',
    ranged: true,
    returnFire: true,
    range: 230,
    fireInterval: 3.2,
    resist: { EXPLOSIVE: 1.6, ENERGY: 0.65 },
  },
  mechpilot: {
    id: 'mechpilot',
    name: 'Mech Pilot',
    desc: 'Elite walker ace. Heavy plating and a shoulder cannon.',
    hp: 1400,
    speed: 34,
    armour: 32,
    bounty: 70,
    breachDamage: 14,
    radius: 18,
    colour: 0xffb060,
    scale: 1.8,
    family: 'human',
    elite: true,
    ranged: true,
    returnFire: true,
    range: 180,
    fireInterval: 2.0,
    resist: { EXPLOSIVE: 1.35, ENERGY: 0.8, BALLISTIC: 0.7 },
  },

  // ── THEMED BOSSES ─────────────────────────────────────────
  hivequeen: {
    id: 'hivequeen',
    name: 'Hive Queen',
    desc: 'Insect matriarch. Broods wasps and floods the lane with locusts.',
    hp: 3200,
    speed: 30,
    armour: 14,
    bounty: 160,
    breachDamage: 18,
    radius: 20,
    colour: 0xc8d44a,
    scale: 2.1,
    family: 'insect',
    boss: true,
    elite: true,
    spawnChild: 'wasp',
    spawnInterval: 1.6,
    spawnMax: 6,
    resist: { THERMAL: 1.7, BALLISTIC: 0.7, ENERGY: 0.9, EXPLOSIVE: 0.85 },
    phases: [
      { hpPct: 0.7, speedMul: 1.2, message: 'HIVE QUEEN — BROOD SURGE' },
      { hpPct: 0.4, summon: 'locust', summonCount: 10, speedMul: 1.35, message: 'HIVE QUEEN RELEASES THE SWARM' },
    ],
  },
  siegebreaker: {
    id: 'siegebreaker',
    name: 'Siegebreaker Titan',
    desc: 'Machine colossus. Plasma battery and a wide-area jamming core.',
    hp: 5200,
    speed: 24,
    armour: 34,
    bounty: 220,
    breachDamage: 26,
    radius: 23,
    colour: 0x7fd4ff,
    scale: 2.4,
    family: 'robot',
    boss: true,
    elite: true,
    shield: 400,
    shieldRegen: 14,
    stunTowers: true,
    stunRadius: 95,
    resist: { BALLISTIC: 0.3, THERMAL: 0.7, EXPLOSIVE: 0.85, ENERGY: 1.6 },
    phases: [
      { hpPct: 0.6, armourMul: 1.2, message: 'SIEGEBREAKER — PLATING LOCKED' },
      { hpPct: 0.3, summon: 'dronebot', summonCount: 5, speedMul: 1.25, message: 'SIEGEBREAKER DEPLOYS DRONES' },
    ],
  },
  warlord: {
    id: 'warlord',
    name: 'Warlord Prime',
    desc: 'Raider commander. Long-range laser battery and a gunner escort.',
    hp: 3000,
    speed: 36,
    armour: 24,
    bounty: 200,
    breachDamage: 20,
    radius: 19,
    colour: 0xffc46a,
    scale: 2.0,
    family: 'human',
    boss: true,
    elite: true,
    ranged: true,
    returnFire: true,
    range: 220,
    fireInterval: 1.4,
    resist: { EXPLOSIVE: 1.5, ENERGY: 0.7, BALLISTIC: 0.8 },
    phases: [
      { hpPct: 0.65, speedMul: 1.2, message: 'WARLORD PRIME — ADVANCING' },
      { hpPct: 0.3, summon: 'gunner', summonCount: 4, speedMul: 1.3, message: 'WARLORD CALLS HIS CREW' },
    ],
  },
  matriarch: {
    id: 'matriarch',
    name: 'Corrosive Matriarch',
    desc: 'Venom brood-mother. Ranged acid that corrodes your turrets.',
    hp: 3600,
    speed: 28,
    armour: 18,
    bounty: 190,
    breachDamage: 22,
    radius: 21,
    colour: 0x9fe04a,
    scale: 2.2,
    family: 'insect',
    boss: true,
    elite: true,
    ranged: true,
    returnFire: true,
    range: 190,
    fireInterval: 1.8,
    spawnChild: 'venomspitter',
    spawnInterval: 3.4,
    spawnMax: 3,
    resist: { THERMAL: 1.6, EXPLOSIVE: 0.85, BALLISTIC: 0.75, ENERGY: 0.95 },
    phases: [
      { hpPct: 0.6, message: 'MATRIARCH — ACID VEIL' },
      { hpPct: 0.28, summon: 'hivecrawler', summonCount: 2, speedMul: 1.2, message: 'MATRIARCH SPLITS THE HIVE' },
    ],
  },
  leviathan: {
    id: 'leviathan',
    name: 'Plasma Leviathan',
    desc: 'Undermesh apex form. Overcharged shields that detonate when popped.',
    hp: 6000,
    speed: 22,
    armour: 26,
    bounty: 260,
    breachDamage: 30,
    radius: 24,
    colour: 0xb06cff,
    scale: 2.6,
    family: 'mesh',
    boss: true,
    elite: true,
    shield: 500,
    shieldRegen: 18,
    regen: 8,
    resist: { BALLISTIC: 0.4, THERMAL: 0.75, EXPLOSIVE: 0.8, ENERGY: 1.45 },
    phases: [
      { hpPct: 0.7, speedMul: 1.15, message: 'LEVIATHAN — SHIELD SURGE' },
      { hpPct: 0.45, summon: 'veilguard', summonCount: 4, speedMul: 1.25, message: 'LEVIATHAN SHEDS ESCORTS' },
      { hpPct: 0.2, speedMul: 1.5, message: 'LEVIATHAN — FINAL FORM' },
    ],
  },
};
